import {
  useGetInterviewApiV1InterviewsInterviewIdGet,
  getGetInterviewApiV1InterviewsInterviewIdGetQueryKey
} from '../api/endpoints/openapi-generated'
import {
  UseQueryResult,
  QueryKey,
  QueryCacheNotifyEvent
} from '@tanstack/react-query'
import {
  GetInterviewResponse,
  type HTTPValidationError,
  InterviewResource
} from '../api/endpoints/openapi-generated.schemas'
import { queryClient } from '../query-client'
import { type ApiError } from '../api/mutator/platform-instance'

type InterviewQueryDataType = GetInterviewResponse

export function getInterviewDataFromEvent(
  event: QueryCacheNotifyEvent,
  interviewId: string
): GetInterviewResponse | null | undefined {
  if (event.type !== 'updated') return

  const queryKey = getInterviewQueryKey(interviewId)
  if (event.query.queryKey.toString() !== queryKey.toString()) return

  const data = event.query.state.data as GetInterviewResponse | undefined
  return data ?? null
}

export function setInterviewQueryData(
  interviewResponse: InterviewQueryDataType
): void {
  const key = getInterviewQueryKey(interviewResponse.data.id)
  queryClient.setQueryData(key, interviewResponse)
}

function getInterviewQueryKey(interviewId: string): QueryKey {
  return getGetInterviewApiV1InterviewsInterviewIdGetQueryKey(interviewId)
}

export function getInterview(
  interviewId: string
): InterviewResource | undefined {
  return queryClient.getQueryData<InterviewQueryDataType>(
    getInterviewQueryKey(interviewId)
  )?.data
}

export function useInterview(
  interviewId: string | null
): UseQueryResult<InterviewQueryDataType, ApiError<HTTPValidationError>> {
  const queryKey = getInterviewQueryKey(interviewId || '')

  const result = useGetInterviewApiV1InterviewsInterviewIdGet<
    InterviewQueryDataType,
    ApiError<HTTPValidationError>
  >(interviewId ?? '', {
    query: {
      queryKey,
      enabled: !!interviewId && !!queryKey,
      retry: (_, err) => {
        if (err.status === 403) return false
        return true
      },
      staleTime: 10_000,
      refetchInterval: 30_000,
      gcTime: 60_000
    }
  })
  return result
}
